import React, { useCallback, useMemo, useRef } from "react";
import { useField } from "formik";
import { Entity } from "@common/types/entity";
import { useClasses } from "@api/class.api";
import { useAnyEntityHasAccrualLedger } from "@api/ledger.api";
import { useCounterpartyNameMap } from "@components/counterparty/useCounterpartyNameMap";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { Button } from "@design-system/Button/Button";
import { PlusIcon } from "@icons";
import {
    SpreadsheetGrid,
    ChangedCells,
} from "@components/spreadsheet/SpreadsheetGrid";
import { SpreadsheetData } from "@components/spreadsheet/types";
import { useScheduleTemplates } from "@api/scheduleTemplate.api";
import {
    optionalSplitDescription,
    resolveSplitLineAccrualAccountId,
    scheduleAccrualHoldingAccountId,
} from "@common/helpers/splitRuleAction";
import { ScheduleTemplateListItem } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { useScope } from "@state/scopeContext.lib";
import {
    FormRuleConditionType,
    TransactionRuleActionFormSchema,
    TransactionRuleConditionFormSchema,
} from "../constants";
import styles from "./RuleSplitSpreadsheet.module.scss";
import { SplitScheduleHoldingPatch } from "./resolveSplitScheduleHolding";
import { useFillMissingSplitScheduleAccrual } from "./useFillMissingSplitScheduleAccrual";
import {
    SplitRow,
    useSplitRuleSpreadsheetColumns,
} from "./useSplitRuleSpreadsheetColumns";

interface Props {
    index: number;
    entities: Entity[];
    isPercentage: boolean;
    isGlFirst?: boolean;
}

function isLiveSplitRow(row: SplitRow): boolean {
    return (
        row._idx !== undefined ||
        Boolean(row.splitDescription) ||
        row.amount !== undefined ||
        Boolean(row.categoryId) ||
        row.entityId !== undefined ||
        Boolean(row.classIds?.length) ||
        Boolean(row.counterpartyId) ||
        Boolean(row.accountId) ||
        Boolean(row.accrualAccountId) ||
        Boolean(row.scheduleTemplateId)
    );
}

function holdingAccountIdForTemplate(params: {
    scheduleTemplates: ScheduleTemplateListItem[];
    templateId: string | undefined;
    entityId: number | undefined;
}): string | undefined {
    if (!params.templateId) {
        return undefined;
    }
    const template = params.scheduleTemplates.find(
        (scheduleTemplate) => scheduleTemplate.id === params.templateId,
    );
    return scheduleAccrualHoldingAccountId({
        accountRows: template?.accountRows ?? [],
        entityId: params.entityId,
    });
}

export const RuleSplitSpreadsheet: React.FC<Props> = ({
    index,
    entities,
    isPercentage,
    isGlFirst = false,
}) => {
    const [field, , helpers] = useField<
        TransactionRuleActionFormSchema["value"]
    >(`actions.${index}.value`);

    const [conditionsField] =
        useField<TransactionRuleConditionFormSchema[]>("conditions");

    const { isActivated: hasClasses } = useClasses();
    const { schedules } = useTypedFlags();
    const { workspaceId } = useScope();
    const { data: scheduleTemplates = [] } = useScheduleTemplates({
        enabled: schedules,
    });
    const schedulePatchByRowIdxRef = useRef<
        Map<number, SplitScheduleHoldingPatch>
    >(new Map());

    const counterpartiesMap = useCounterpartyNameMap();

    const splitActionRef = useRef(field.value?.splitAction);
    splitActionRef.current = field.value?.splitAction;

    const formSplits = field.value?.splitAction?.splits;
    const splits = formSplits ?? [];

    const entityIds = useMemo(
        () => entities.map((entity) => entity.id),
        [entities],
    );
    const hasAccrualLedger = useAnyEntityHasAccrualLedger(entityIds);

    const { columns, gridTemplateColumns } = useSplitRuleSpreadsheetColumns({
        entities,
        isPercentage,
        isGlFirst,
        hasClasses,
        showAccrualColumn: hasAccrualLedger,
        showScheduleColumn: schedules,
        counterpartiesMap,
        splitsLength: splits.length,
        schedulePatchByRowIdxRef,
    });

    const isCashOutByAmount =
        !isPercentage &&
        conditionsField.value.some(
            (condition) =>
                condition.conditionType === FormRuleConditionType.AMOUNT &&
                !condition.amountIsCredit,
        ) &&
        splits.some((split) => (split.amount ?? 0) > 0);

    const data: SpreadsheetData = useMemo(
        () =>
            (formSplits ?? []).map((split, idx) => ({
                _idx: idx,
                splitDescription: split.splitDescription ?? null,
                amount: split.amount ?? null,
                percentageAmount: split.percentageAmount ?? null,
                categoryId: split.categoryId,
                entityId: split.entityId,
                classIds: split.classIds ?? [],
                counterpartyId: split.counterpartyId ?? null,
                accountId: split.accountId ?? null,
                accrualAccountId: split.accrualAccountId ?? null,
                scheduleTemplateId: split.scheduleTemplateId ?? null,
            })),
        [formSplits],
    );

    const isCellEditable = useCallback(
        (rowIdx: number, colIdx: number): boolean => {
            if (colIdx === 0) {
                if (!isPercentage && rowIdx === splits.length - 1) {
                    return false;
                }
                return splits.length > 2;
            }
            const isLastRowPercentageCell =
                isPercentage &&
                rowIdx === splits.length - 1 &&
                columns[colIdx]?.field === "percentageAmount";

            const isLastRowAmountCell =
                !isPercentage &&
                rowIdx === splits.length - 1 &&
                columns[colIdx]?.field === "amount";

            return !isLastRowPercentageCell && !isLastRowAmountCell;
        },
        [splits.length, isPercentage, columns],
    );

    const handleChange = useCallback(
        (newData: SpreadsheetData, _changedCells: ChangedCells) => {
            const rows = newData as SplitRow[];

            const liveSplits = rows.filter(isLiveSplitRow).map((row) => {
                const scheduleTemplateId = row.scheduleTemplateId ?? undefined;
                const pendingPatch =
                    row._idx === undefined
                        ? undefined
                        : schedulePatchByRowIdxRef.current.get(row._idx);
                if (row._idx !== undefined) {
                    schedulePatchByRowIdxRef.current.delete(row._idx);
                }
                const holdingAccountId = holdingAccountIdForTemplate({
                    scheduleTemplates,
                    templateId: scheduleTemplateId,
                    entityId: pendingPatch?.entityId ?? row.entityId,
                });
                const previousSplit =
                    row._idx === undefined
                        ? undefined
                        : splitActionRef.current?.splits?.[row._idx];
                const clearedHoldingAccountId = scheduleTemplateId
                    ? undefined
                    : holdingAccountIdForTemplate({
                          scheduleTemplates,
                          templateId:
                              previousSplit?.scheduleTemplateId ?? undefined,
                          entityId: previousSplit?.entityId ?? row.entityId,
                      });
                const accrualAccountId = resolveSplitLineAccrualAccountId({
                    hasSchedule: Boolean(scheduleTemplateId),
                    accrualAccountId: row.accrualAccountId,
                    scheduleHoldingAccountId:
                        pendingPatch?.accrualAccountId ?? holdingAccountId,
                    clearedScheduleHoldingAccountId: clearedHoldingAccountId,
                });

                return {
                    splitDescription: optionalSplitDescription(
                        row.splitDescription,
                    ),
                    categoryId: row.categoryId,
                    entityId: pendingPatch?.entityId ?? row.entityId,
                    classIds: row.classIds,
                    counterpartyId: row.counterpartyId,
                    accountId: row.accountId ?? undefined,
                    accrualAccountId,
                    scheduleTemplateId,
                    ...(isPercentage
                        ? { percentageAmount: row.percentageAmount }
                        : { amount: row.amount }),
                };
            });

            helpers.setValue({
                splitAction: {
                    ...splitActionRef.current,
                    splits: liveSplits,
                },
            });
        },
        [helpers, isPercentage, scheduleTemplates],
    );

    const fillMissingScheduleAccrual = useCallback(
        (nextSplits: Array<(typeof splits)[number]>) => {
            helpers.setValue({
                splitAction: {
                    ...splitActionRef.current,
                    splits: nextSplits,
                },
            });
        },
        [helpers],
    );

    useFillMissingSplitScheduleAccrual({
        splits,
        scheduleTemplates,
        workspaceId,
        onSplitsFilled: fillMissingScheduleAccrual,
    });

    const handleAddSplit = useCallback(() => {
        const currentSplits = field.value?.splitAction?.splits ?? [];

        helpers.setValue({
            splitAction: {
                ...field.value?.splitAction,
                splits: [
                    ...currentSplits,
                    isPercentage ? { percentageAmount: 0 } : {},
                ],
            },
        });
    }, [field.value, helpers, isPercentage]);

    return (
        <div className={styles.wrapper}>
            <SpreadsheetGrid
                data={data}
                columns={columns}
                onChange={handleChange}
                isCellEditable={isCellEditable}
                showTotalsRow={false}
                stickyHeader
                noOuterBorder
                lightNotEditableCells
                showDropdownIndicator
                gridTemplateColumns={gridTemplateColumns}
                className={styles.spreadsheetGrid}
            />
            <div className={styles.footer}>
                <Button
                    variant="primary"
                    size="md"
                    leftIcon={<PlusIcon />}
                    type="button"
                    onClick={handleAddSplit}
                >
                    Add split
                </Button>
                {isCashOutByAmount && (
                    <div className={styles.cashOutWarning}>
                        Cash out split amounts are typically negative
                    </div>
                )}
            </div>
        </div>
    );
};
